import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { showNotification } from '@mantine/notifications'
import type { OrpcClient } from '@/orpc/client'
import { useOrpc } from './useClient'

export type MyProfile = Awaited<ReturnType<OrpcClient['getMyProfile']>>

/**
 * The signed-in Org Member for My Profile (`GET /v2/practitioners/me/profile`): their
 * Provider Operations row without the integration mappings, plus photo, mobile phone, the
 * console's role label, their Organizations and whether they are clinical. The middleware
 * resolves the member from the session; nothing is sent.
 */
export function useMyProfile() {
  const orpc = useOrpc()
  return useQuery(orpc.getMyProfile.queryOptions())
}

/**
 * The same read as last fetched, without fetching: the Shell header follows a save on My
 * Profile through it, since the session profile Medplum caches only refreshes on sign-in.
 * `undefined` until the member has opened My Profile.
 */
export function useMyProfileIfLoaded(): MyProfile | undefined {
  const orpc = useOrpc()
  return useQuery({ ...orpc.getMyProfile.queryOptions(), enabled: false }).data
}

/**
 * Writes the member's own names, mobile phone and photo (`POST /v2/practitioners/me/profile`).
 * A success refetches the read before resolving, so a form resets to what was saved and
 * the summary card and the Shell header show it at once.
 */
export function useUpdateMyProfile() {
  const orpc = useOrpc()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.updateMyProfile.mutationOptions({
      onSuccess: async () => {
        showNotification({
          color: 'green',
          title: 'Profile updated',
          message: 'Your changes were saved.',
        })
        await queryClient.invalidateQueries({ queryKey: orpc.getMyProfile.key() })
      },
      onError: (error) => {
        showNotification({
          color: 'red',
          title: 'Profile not saved',
          message: error instanceof Error ? error.message : 'Unknown error',
        })
      },
    }),
  )
}

export type UpdateMyCredentialsInput = Parameters<OrpcClient['updateMyCredentials']>[0]
/** A state license as its holder re-sends it: the self-editable fields only. */
export type SelfLicenseRow = NonNullable<UpdateMyCredentialsInput['licenses']>[number]

/**
 * Writes the member's own Credentials — NPI, Professional Role, Specialty and licenses
 * (`POST /v2/practitioners/me/profile/credentials`). The middleware refuses a member without
 * an active Medical doctor role. A success refetches the read before resolving, as the
 * profile write does.
 */
export function useUpdateMyCredentials() {
  const orpc = useOrpc()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.updateMyCredentials.mutationOptions({
      onSuccess: async () => {
        showNotification({
          color: 'green',
          title: 'Credentials updated',
          message: 'Your changes were saved.',
        })
        await queryClient.invalidateQueries({ queryKey: orpc.getMyProfile.key() })
      },
      onError: (error) => {
        showNotification({
          color: 'red',
          title: 'Credentials not saved',
          message: error instanceof Error ? error.message : 'Unknown error',
        })
      },
    }),
  )
}

/**
 * Changes the member's own password (`POST /v2/auth/change-password`). A success confirms;
 * a refusal is the tab's to place: the error's `data.field` names the field it belongs to.
 */
export function useChangePassword() {
  const orpc = useOrpc()

  return useMutation(
    orpc.authChangePassword.mutationOptions({
      onSuccess: () => {
        showNotification({
          color: 'green',
          title: 'Password changed',
          message: 'Your new password is in effect.',
        })
      },
    }),
  )
}
